<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <!-- Card header -->
          <div class="pb-0 card-header">
            <div class="d-lg-flex">
              <div>
                <h5 class="mb-0">角色管理</h5>
                <p class="mb-0 text-sm">管理用戶角色和權限</p>
              </div>
            </div>
          </div>

          <div class="px-0 pb-0 card-body">
            <div class="table-responsive">
              <table class="table table-flush" id="roles-table">
                <thead class="thead-light">
                  <tr>
                    <th>用戶</th>
                    <th>郵箱</th>
                    <th>當前角色</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in users" :key="user.id">
                    <td class="text-sm font-weight-normal">{{ user.name }}</td>
                    <td class="text-sm font-weight-normal">{{ user.email }}</td>
                    <td>
                      <span
                        class="badge badge-sm"
                        :class="getRoleBadgeClass(user.role)"
                      >
                        {{ getRoleLabel(user.role) }}
                      </span>
                    </td>
                    <td>
                      <select
                        class="form-control form-control-sm"
                        style="width: 150px"
                        :value="user.role"
                        @change="updateUserRole(user, $event.target.value)"
                        :disabled="
                          user.id === currentUser?.id || !can('staff.manage')
                        "
                      >
                        <option value="user">一般用戶</option>
                        <option value="seller">賣家</option>
                        <option value="admin">管理員</option>
                        <option value="super_admin">超級管理員</option>
                      </select>
                      <small
                        v-if="user.id === currentUser?.id"
                        class="text-muted d-block"
                      >
                        無法修改自己的角色
                      </small>
                    </td>
                  </tr>

                  <tr v-if="users.length === 0 && !loading">
                    <td colspan="4" class="text-center py-4 text-secondary">
                      暫無資料
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div
              class="d-flex justify-content-between align-items-center px-3 py-3"
            >
              <div class="text-sm text-muted">
                顯示 {{ users.length }} 個用戶
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";
import Swal from "sweetalert2";
import { hasPermission } from "@/utils/permissions";

export default {
  name: "RoleManagement",
  data() {
    return {
      users: [],
      currentUser: null,
      loading: false,
    };
  },
  mounted() {
    this.currentUser = this.getCurrentUser();
    this.fetchUsers();
  },
  methods: {
    can(permission) {
      return hasPermission(this.currentUser, permission);
    },
    getCurrentUser() {
      try {
        const userStr = localStorage.getItem("user");
        return userStr ? JSON.parse(userStr) : null;
      } catch (error) {
        console.error("Error getting current user:", error);
        return null;
      }
    },

    async fetchUsers() {
      this.loading = true;
      try {
        const response = await request.get("/api/admin/users", {
          params: {
            per_page: 100,
          },
        });
        this.users = response.data.data;
      } catch (error) {
        console.error("Error fetching users:", error);
        Swal.fire({
          icon: "error",
          title: "錯誤",
          text: "無法載入用戶列表",
        });
      } finally {
        this.loading = false;
      }
    },

    async updateUserRole(user, newRole) {
      try {
        const result = await Swal.fire({
          title: "確認修改角色",
          html: `確定要將 <strong>${
            user.name
          }</strong> 的角色從 <strong>${this.getRoleLabel(
            user.role
          )}</strong> 改為 <strong>${this.getRoleLabel(newRole)}</strong> 嗎？`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "確定",
          cancelButtonText: "取消",
        });

        if (result.isConfirmed) {
          await request.put(`/api/admin/users/${user.id}`, {
            role: newRole,
          });

          this.fetchUsers();

          Swal.fire({
            icon: "success",
            title: "成功",
            text: "角色已更新",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          // Reset select to original value
          this.fetchUsers();
        }
      } catch (error) {
        console.error("Error updating user role:", error);
        Swal.fire({
          icon: "error",
          title: "錯誤",
          text: error.response?.data?.message || "無法更新角色",
        });
        this.fetchUsers();
      }
    },

    getRoleLabel(role) {
      const labels = {
        user: "一般用戶",
        seller: "賣家",
        admin: "管理員",
        super_admin: "超級管理員",
      };
      return labels[role] || role;
    },

    getRoleBadgeClass(role) {
      const classes = {
        user: "badge-secondary",
        seller: "badge-info",
        admin: "badge-warning",
        super_admin: "badge-success",
      };
      return classes[role] || "badge-secondary";
    },
  },
};
</script>

<style scoped>
.badge {
  font-size: 0.75rem;
  padding: 0.35em 0.65em;
}
</style>
